export type MpQrPaymentPhase =
  | 'idle'
  | 'generating'
  | 'waiting_scan'
  | 'approved'
  | 'expired'
  | 'rejected'
  | 'error'
  | 'voided';

export type MpQrPaymentSnapshot = {
  salePaymentStatus?: string | null;
  salePaymentRowStatus?: string | null;
  paymentReconciliationStatus?: string | null;
  expiresAt?: string | null;
  qrPending?: boolean;
  saleVoided?: boolean;
};

const PAID_STATUSES = new Set(['paid', 'approved']);
const EXPIRED_STATUSES = new Set(['expired']);
const REJECTED_STATUSES = new Set(['cancelled', 'failed', 'refunded', 'rejected']);

export function deriveMpQrPhase(snapshot: MpQrPaymentSnapshot, nowMs = Date.now()): MpQrPaymentPhase {
  if (snapshot.saleVoided) return 'voided';

  const reconciliation = snapshot.paymentReconciliationStatus?.toLowerCase() ?? '';
  const saleStatus = snapshot.salePaymentStatus?.toLowerCase() ?? '';
  const rowStatus = snapshot.salePaymentRowStatus?.toLowerCase() ?? '';

  if (reconciliation === 'approved' || rowStatus === 'confirmed' || PAID_STATUSES.has(saleStatus)) {
    return 'approved';
  }

  if (EXPIRED_STATUSES.has(saleStatus)) return 'expired';

  if (snapshot.expiresAt) {
    const expiresMs = new Date(snapshot.expiresAt).getTime();
    if (Number.isFinite(expiresMs) && expiresMs <= nowMs) {
      return 'expired';
    }
  }

  const salePendingLike = saleStatus === 'pending' || saleStatus === 'created';
  if (
    REJECTED_STATUSES.has(rowStatus) &&
    salePendingLike &&
    !snapshot.qrPending &&
    snapshot.expiresAt
  ) {
    const expiresMs = new Date(snapshot.expiresAt).getTime();
    if (Number.isFinite(expiresMs) && expiresMs > nowMs) {
      return 'waiting_scan';
    }
  }

  if (REJECTED_STATUSES.has(saleStatus) || REJECTED_STATUSES.has(rowStatus)) {
    return 'rejected';
  }

  if (snapshot.qrPending) return 'generating';

  return 'waiting_scan';
}

export function mpQrPollIntervalMs(elapsedMs: number): number {
  if (elapsedMs < 2 * 60 * 1000) return 3000;
  if (elapsedMs < 10 * 60 * 1000) return 8000;
  return 15000;
}

/** Cada N polls, usar getSaleQr (heavy, sincroniza con MP API) en vez del RPC liviano. */
export const MP_QR_HEAVY_POLL_EVERY = 3;

export function mpQrCountdownSeconds(expiresAt: string | null | undefined, nowMs = Date.now()): number | null {
  if (!expiresAt) return null;
  const expiresMs = new Date(expiresAt).getTime();
  if (!Number.isFinite(expiresMs)) return null;
  return Math.max(0, Math.ceil((expiresMs - nowMs) / 1000));
}

export const MP_QR_AUTO_CLOSE_MS = 3000;
export const MP_QR_TTL_SECONDS = 15 * 60;

/** True when closing the modal should show confirmation instead of dismissing immediately. */
export function shouldShowMpQrCloseConfirm(phase: MpQrPaymentPhase): boolean {
  // Toda fase con venta activa + cobro pendiente: cerrar sin confirmar deja
  // la venta zombie (activa con pago pending y sin camino de recuperación).
  return (
    phase === 'waiting_scan' ||
    phase === 'generating' ||
    phase === 'error' ||
    phase === 'expired' ||
    phase === 'rejected'
  );
}
